import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ShieldCheck, Truck, RefreshCw, MessageCircle, Mountain, Wrench, Leaf } from "lucide-react";
import { motion } from "framer-motion";
import { api, CATEGORY_NAMES } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { HeroShowcase } from "../components/HeroShowcase";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const THULE_FACTS = [
  { icon: Mountain, title: "Din 1942, în Suedia", text: "Thule a pornit în Hillerstorp, Suedia, și a devenit un reper mondial pentru produse care te ajută să iei familia cu tine, oriunde." },
  { icon: Wrench, title: "Testat în Thule Test Center", text: "Produsele Thule sunt supuse unor teste extreme de rezistență, siguranță și durabilitate înainte de a ajunge la familii." },
  { icon: Leaf, title: "Design scandinav, materiale durabile", text: "Linii curate, funcționalitate și materiale alese pentru a rezista anilor – nu doar unui sezon." },
];

const USP = [
  { icon: ShieldCheck, title: "Exclusiv THULE", text: "Un singur brand, selectat cu grijă. Fiecare produs este original și documentat." },
  { icon: Truck, title: "Livrare atentă", text: "Condițiile de livrare vor fi comunicate la finalizarea comenzii. Fără promisiuni nefondate." },
  { icon: RefreshCw, title: "Consultanță la alegere", text: "Te ajutăm să alegi între Sleek, Spring, Urban Glide sau Chariot în funcție de stilul tău." },
  { icon: MessageCircle, title: "Răspuns rapid", text: "Suport dedicat înainte și după achiziție, pentru montaj, compatibilități și întreținere." },
];

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } };

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [faq, setFaq] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/faq").then((r) => setFaq(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      <section className="container-cc pt-10 lg:pt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <motion.div className="lg:col-span-5 order-2 lg:order-1 pb-6" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <div className="flex items-center gap-4 mb-6">
              <p className="eyebrow">Boutique online dedicat</p>
              <img src="/thule-logo.png" alt="Thule Sweden" data-testid="hero-thule-logo" className="h-5 w-auto" />
            </div>
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-[64px] leading-[0.98] tracking-tight">
              Cărucioare și <em className="not-italic text-[#B4A38E]">scaune auto</em> premium.
            </h1>
            <p className="mt-8 text-base text-[#5A5A5A] leading-relaxed max-w-md">
              Cărucioare 2 în 1, all-terrain, remorci multisport și sistemul auto Maple + Alfi, pentru fiecare etapă a copilului.
            </p>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="hero-category-chips">
              {[["carucioare", "Urbane & 2 în 1"], ["sport", "All-terrain"], ["multisport", "Multisport"], ["scaune-auto", "Scaune auto"]].map(([slug, label]) => (
                <Link key={slug} to={`/catalog/${slug}`} data-testid={`hero-chip-${slug}`} className="h-9 px-4 inline-flex items-center border border-black/15 text-[12px] uppercase tracking-[0.14em] font-semibold hover:border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200">{label}</Link>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/catalog" data-testid="hero-cta-catalog" className="btn-primary">Descoperă colecția <ArrowRight size={16} /></Link>
              <Link to="/comparare" data-testid="hero-cta-compare" className="btn-outline">Compară modelele</Link>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
              {[["0+", "de la naștere"], ["22", "kg greutate maximă"], ["15", "modele THULE"]].map(([n, l]) => (
                <div key={l}>
                  <p className="font-serif text-3xl sm:text-4xl">{n}</p>
                  <p className="text-[11px] uppercase tracking-[0.16em] text-[#737373] mt-1">{l}</p>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div className="hidden lg:block lg:col-span-7 order-1 lg:order-2" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <HeroShowcase />
          </motion.div>
        </div>
      </section>

      <section className="border-y border-black/5 mt-16 lg:mt-24 py-4 overflow-hidden surface">
        <div className="flex whitespace-nowrap marquee">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-12 pr-12 text-[11px] uppercase tracking-[0.3em] text-[#737373]">
              {["Sleek 2", "Spring 2", "Charm 2 în 1", "Urban Glide 4", "Chariot Sport 2", "Chariot Cross 2", "Maple · Elm · Palm", "Sapling", "Bring your life"].map((t) => (
                <span key={t} className="flex items-center gap-12">{t}<span className="h-1 w-1 bg-[#B4A38E] rounded-full" /></span>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="container-cc py-20 lg:py-28" data-testid="home-categories">
        <motion.div {...fade} className="flex items-end justify-between mb-10">
          <div>
            <p className="eyebrow mb-3">Colecții</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">Alege după felul în care trăiești</h2>
          </div>
          <Link to="/catalog" className="btn-ghost hidden sm:inline-flex">Tot catalogul <ArrowRight size={14} /></Link>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {categories.map((c, i) => (
            <motion.div key={c.slug} {...fade} transition={{ ...fade.transition, delay: i * 0.08 }} className={i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : "md:col-span-4"}>
              <Link to={`/catalog/${c.slug}`} data-testid={`category-tile-${c.slug}`} className="group relative block overflow-hidden h-[320px] md:h-[420px] surface">
                <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-contain p-10 pb-28 product-img transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                <div className="absolute left-6 right-6 bottom-6 flex items-end justify-between text-[#0A0A0A]">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#737373]">{c.short} · {c.count} {c.count === 1 ? "produs" : "produse"}</p>
                    <p className="font-serif text-3xl sm:text-4xl mt-1">{c.name}</p>
                  </div>
                  <span className="h-11 w-11 flex items-center justify-center border border-[#0A0A0A] transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight size={18} /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="surface py-20 lg:py-28" data-testid="home-featured">
        <div className="container-cc">
          <motion.div {...fade} className="flex items-end justify-between mb-12">
            <div>
              <p className="eyebrow mb-3">Selecție Cassia</p>
              <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">Produse recomandate</h2>
            </div>
            <Link to="/catalog" className="btn-ghost hidden sm:inline-flex">Vezi toate <ArrowRight size={14} /></Link>
          </motion.div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
            {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        </div>
      </section>

      <section className="container-cc py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-testid="home-story">
        <motion.div {...fade} className="lg:col-span-5">
          <p className="eyebrow mb-3">Filosofia Cassia</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-tight">Mai puține produse. Alese mai bine.</h2>
          <p className="mt-6 text-[#5A5A5A] leading-relaxed">
            Nu suntem un marketplace. Suntem un boutique care a ales un singur brand – THULE – pentru că îmbină ingineria suedeză cu un design care îmbătrânește frumos. Fiecare model din catalog este unul pe care l-am recomanda propriei familii.
          </p>
          <Link to="/despre" data-testid="story-cta" className="btn-ghost mt-8">Povestea noastră <ArrowRight size={14} /></Link>
        </motion.div>
        <motion.div {...fade} className="lg:col-span-7 grid grid-cols-2 gap-4">
          <Link to="/produs/set-carucior-thule-charm-2-in-1-sport-landou-darkest-blue" className="surface aspect-[4/5] flex items-center justify-center p-8 group overflow-hidden">
            <img src="https://aleo.ro/media/catalog/product/cache/e12079e20fd1e2b418c0a812acc403bd/4/7/473b27afb11770459b6d59bf50331da4eb2e39c3a42d3fec202f9e5be28168b8.jpeg" alt="Set Thule Charm 2 în 1" className="product-img max-h-full object-contain transition-transform duration-700 group-hover:scale-105" />
          </Link>
          <Link to="/produs/carucior-thule-spring-2-mid-blue" className="surface aspect-[4/5] flex items-center justify-center p-8 group overflow-hidden mt-12">
            <img src="https://aleo.ro/media/catalog/product/cache/e12079e20fd1e2b418c0a812acc403bd/2/7/273b27cd319766529dc16ff15fb687ee22ddeb946fe0a04c2535d9e3f56c8209.jpeg" alt="Thule Spring 2 Mid Blue" className="product-img max-h-full object-contain transition-transform duration-700 group-hover:scale-105" />
          </Link>
        </motion.div>
      </section>

      <section className="border-y border-black/5" data-testid="home-usp">
        <div className="container-cc grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-black/5">
          {USP.map(({ icon: Icon, title, text }, i) => (
            <motion.div key={title} {...fade} transition={{ ...fade.transition, delay: i * 0.08 }} className="py-10 lg:px-8 first:pl-0 last:pr-0">
              <Icon size={22} strokeWidth={1.2} className="text-[#B4A38E]" />
              <p className="font-serif text-2xl mt-5">{title}</p>
              <p className="text-sm text-[#5A5A5A] mt-2 leading-relaxed">{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="container-cc py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="home-faq">
        <motion.div {...fade} className="lg:col-span-4">
          <p className="eyebrow mb-3">Întrebări frecvente</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">Ce ne întreabă părinții</h2>
          <p className="mt-4 text-sm text-[#5A5A5A]">Nu ai găsit răspunsul? <Link to="/contact" className="underline underline-offset-4">Scrie-ne</Link>.</p>
        </motion.div>
        <motion.div {...fade} className="lg:col-span-8">
          <Accordion type="single" collapsible className="border-t border-black/10">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-black/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-serif text-xl sm:text-2xl font-normal text-left py-6 hover:no-underline hover:text-[#B4A38E] transition-colors">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#5A5A5A] text-sm sm:text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </section>

      <section className="lg:bg-[#0A0A0A]" data-testid="home-thule-brand">
        <div className="container-cc pb-8 lg:pb-0">
        <motion.div {...fade} className="bg-[#0A0A0A] text-white px-8 py-16 lg:px-0 lg:py-24">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
            <div className="max-w-2xl">
              <img src="/thule-logo.png" alt="Thule Sweden" data-testid="brand-thule-logo" className="h-9 w-auto invert mb-8" />
              <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">Bring your life.</h2>
              <p className="mt-5 text-white/60 leading-relaxed">Thule construiește de peste 80 de ani produse care îți permit să trăiești activ, împreună cu cei mici: cărucioare, remorci multisport, rucsacuri de transport și scaune auto, gândite să funcționeze ca un sistem.</p>
            </div>
            <Link to="/catalog" data-testid="brand-cta" className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] px-7 h-12 text-[13px] uppercase tracking-[0.16em] font-semibold hover:bg-[#EBEAE5] transition-colors duration-200 shrink-0">Explorează sistemul Thule <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14 pt-10 border-t border-white/10">
            {THULE_FACTS.map(({ icon: Icon, title, text }) => (
              <div key={title}>
                <Icon size={22} strokeWidth={1.2} className="text-[#B4A38E]" />
                <p className="font-serif text-2xl mt-4">{title}</p>
                <p className="text-sm text-white/55 mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </motion.div>
        </div>
      </section>
      {Object.keys(CATEGORY_NAMES).length === 0 && null}
    </div>
  );
}
